import { useDataProvider, useGetIdentity } from "ra-core";
import { useCallback, useMemo } from "react";

import type { Bookmark, Company, Contact, Tag } from "../types";
import type { CrmDataProvider } from "../providers/supabase/dataProvider";
import { resolveTagsByName, type TagCache } from "../tags/resolveTagsByName";
import { isValidContactEmail, normalizeContactEmail } from "./contactEmail";
import {
  COMPANY_SIMILARITY_WARN_THRESHOLD,
  companySimilarity,
  isShowLeadRow,
  mapShowLeadRow,
  normalizeCompanyName,
  type LoopsImportContact,
} from "./showLeadImport";

export type ContactImportSchema = {
  first_name: string;
  last_name: string;
  gender: string;
  title: string;
  company: string;
  email: string;
  phone: string;
  background: string;
  avatar: string;
  first_seen: string;
  last_seen: string;
  status: string;
  tags: string;
  linkedin_url: string;
  // Optional: the JSON array the contact export writes, e.g.
  // [{"label":"Profile","url":"https://www.linkedin.com/in/ada"}]. LCATOMICZO-83.
  bookmarks?: string;
  // Optional: lead/customer/partner/network. Show-lead imports set "lead".
  record_type?: string;
};

export type ContactImportOptions = {
  // When a show-lead (Loops-format) CSV is detected, also create each contact
  // in Loops with full properties and stamp loops_contact_id back on the CRM
  // row. Requires LOOPS_API_KEY on the backend.
  pushToLoops?: boolean;
};

export function useContactImport({
  pushToLoops = false,
}: ContactImportOptions = {}) {
  const today = new Date().toISOString();
  const user = useGetIdentity();
  const dataProvider = useDataProvider();

  // company cache to avoid creating the same company multiple times and costly roundtrips
  // Cache is dependent of dataProvider, so it's safe to use it as a dependency
  const companiesCache = useMemo(
    () => new Map<string, Company>(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataProvider],
  );
  // Company matching policy (see showLeadImport.ts): exact name, then
  // case-insensitive, then normalized (punctuation/suffix-stripped). Only
  // rule-provable matches link to an existing company; everything else is
  // created, with a warning when a similar-but-unproven name already exists
  // so the periodic cleanup pass can review the pair. We load the full
  // company list once per import (a couple thousand rows at most) because
  // normalized matching cannot be expressed as a backend filter.
  const allCompaniesRef = useMemo(
    () => ({ loaded: false }) as { loaded: boolean; all?: Company[] },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataProvider],
  );
  const getCompanies = useCallback(
    async (names: string[]) => {
      const warnings: string[] = [];
      const trimmedNames = [
        ...new Set(names.map((name) => name.trim()).filter(Boolean)),
      ];
      const result = new Map<string, Company>();
      const misses: string[] = [];

      for (const name of trimmedNames) {
        const cached = companiesCache.get(name);
        if (cached) result.set(name, cached);
        else misses.push(name);
      }

      if (misses.length > 0) {
        if (!allCompaniesRef.loaded) {
          const { data } = await dataProvider.getList<Company>("companies", {
            filter: {},
            pagination: { page: 1, perPage: 10000 },
            sort: { field: "id", order: "ASC" },
          });
          allCompaniesRef.all = data;
          allCompaniesRef.loaded = true;
        }
        const all = allCompaniesRef.all ?? [];
        const byExact = new Map(all.map((c) => [c.name.trim(), c]));
        const byLower = new Map(
          all.map((c) => [c.name.trim().toLowerCase(), c]),
        );
        const byNormalized = new Map(
          all.map((c) => [normalizeCompanyName(c.name), c]),
        );

        for (const name of misses) {
          const match =
            byExact.get(name) ??
            byLower.get(name.toLowerCase()) ??
            byNormalized.get(normalizeCompanyName(name));
          if (match) {
            companiesCache.set(name, match);
            result.set(name, match);
            if (match.name.trim() !== name) {
              warnings.push(
                `Company "${name}" matched existing "${match.name}".`,
              );
            }
            continue;
          }

          // No rule-provable match: create, but surface near-misses.
          const similar = all
            .map((c) => ({ c, score: companySimilarity(name, c.name) }))
            .filter(({ score }) => score >= COMPANY_SIMILARITY_WARN_THRESHOLD)
            .sort((a, b) => b.score - a.score)[0];
          const response = await dataProvider.create("companies", {
            data: {
              name,
              created_at: new Date().toISOString(),
              sales_id: user?.identity?.id,
            },
          });
          const created = response.data as Company;
          companiesCache.set(name, created);
          allCompaniesRef.all = [...(allCompaniesRef.all ?? []), created];
          result.set(name, created);
          if (similar) {
            warnings.push(
              `Company "${name}" created as new — review: similar to existing "${similar.c.name}".`,
            );
          }
        }
      }

      return { companies: result, warnings };
    },
    [companiesCache, allCompaniesRef, user?.identity?.id, dataProvider],
  );

  // Tags cache to avoid creating the same tag multiple times and costly roundtrips.
  // Keyed by the normalised name, matching the unique index on tags.
  // Cache is dependent of dataProvider, so it's safe to use it as a dependency
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tagsCache = useMemo<TagCache>(() => new Map(), [dataProvider]);
  const getTags = useCallback(
    async (names: string[]) =>
      resolveTagsByName({
        dataProvider,
        names,
        cache: tagsCache,
        colorForNewTag: () => "#f9f9f9",
      }),
    [tagsCache, dataProvider],
  );

  // Return the existing contacts for the given emails, keyed by normalized
  // email. Classic imports skip these rows; show-lead imports merge into them
  // (a returning NAB lead scanned again at SIGGRAPH should gain the new tags,
  // not be dropped). Emails are normalized (trim + lowercase) to match how
  // they are stored, since email is our idempotency key (one email per contact).
  const getExistingContacts = useCallback(
    async (emails: string[]): Promise<Map<string, Contact>> => {
      const normalized = [
        ...new Set(
          emails.map((email) => email.trim().toLowerCase()).filter(Boolean),
        ),
      ];
      if (normalized.length === 0) return new Map();

      const { data } = await dataProvider.getList<Contact>("contacts", {
        filter: {
          "email@in": `(${normalized.map((email) => `"${email}"`).join(",")})`,
        },
        pagination: { page: 1, perPage: normalized.length },
        sort: { field: "id", order: "ASC" },
      });

      return new Map(
        data
          .filter((contact) => contact.email)
          .map((contact) => [contact.email.trim().toLowerCase(), contact]),
      );
    },
    [dataProvider],
  );

  const processBatch = useCallback(
    async (batch: ContactImportSchema[]) => {
      const errors: string[] = [];

      // Show-lead (Loops-format) CSV detection: map rows into the classic
      // schema and keep the Loops payload for the optional push.
      const showLead = batch.length > 0 && isShowLeadRow(batch[0]);
      const loopsByEmail = new Map<string, LoopsImportContact>();
      if (showLead) {
        batch = batch.map((raw) => {
          const mapped = mapShowLeadRow(raw as Record<string, string>);
          loopsByEmail.set(mapped.crm.email.trim().toLowerCase(), mapped.loops);
          return mapped.crm;
        });
      }

      const seenEmails = new Set<string>();
      const candidates = batch.flatMap((contact) => {
        const normalizedEmail = normalizeContactEmail(contact.email);
        const label =
          [contact.first_name, contact.last_name].filter(Boolean).join(" ") ||
          "Unnamed contact";

        if (!normalizedEmail) {
          errors.push(`${label}: email is required.`);
          return [];
        }
        if (!isValidContactEmail(normalizedEmail)) {
          errors.push(`${label}: "${normalizedEmail}" is not a valid email.`);
          return [];
        }
        if (seenEmails.has(normalizedEmail)) {
          errors.push(
            `Duplicate email "${normalizedEmail}" in this CSV batch.`,
          );
          return [];
        }

        seenEmails.add(normalizedEmail);
        return [{ ...contact, email: normalizedEmail }];
      });

      const existingContacts = await getExistingContacts(
        candidates.map((contact) => contact.email),
      );
      const toCreate = candidates.filter((contact) => {
        if (!existingContacts.has(contact.email)) return true;
        if (!showLead) {
          errors.push(`Email "${contact.email}" already exists in the CRM.`);
        }
        return false;
      });
      // Show-lead rows for people already in the CRM are merged, not skipped.
      const toMerge = showLead
        ? candidates.filter((contact) => existingContacts.has(contact.email))
        : [];

      const [{ companies, warnings: companyWarnings }, tags] =
        await Promise.all([
          getCompanies(
            [...toCreate, ...toMerge]
              .map((contact) => contact.company?.trim())
              .filter((name): name is string => !!name),
          ),
          getTags(
            [...toCreate, ...toMerge].flatMap((contact) =>
              parseTags(contact.tags),
            ),
          ),
        ]);
      errors.push(...companyWarnings);

      const results = await Promise.allSettled(
        toCreate.map(
          async ({
            first_name,
            last_name,
            gender,
            title,
            email,
            phone,
            background,
            first_seen,
            last_seen,
            status,
            company: companyName,
            tags: tagNames,
            linkedin_url,
            bookmarks,
            record_type,
          }) => {
            const company = companyName?.trim()
              ? companies.get(companyName.trim())
              : undefined;
            const tagList = parseTags(tagNames)
              .map((name) => tags.get(name))
              .filter((tag): tag is Tag => !!tag);

            return dataProvider.create("contacts", {
              data: {
                first_name,
                last_name,
                gender,
                title,
                email,
                phone,
                background,
                first_seen: first_seen
                  ? new Date(first_seen).toISOString()
                  : today,
                last_seen: last_seen
                  ? new Date(last_seen).toISOString()
                  : today,
                status,
                company_id: company?.id,
                tags: tagList.map((tag) => tag.id),
                sales_id: user?.identity?.id,
                linkedin_url,
                bookmarks: parseBookmarks(bookmarks),
                ...(record_type ? { record_type } : {}),
              },
            });
          },
        ),
      );

      // Merge show-lead rows into contacts that already exist: union tags,
      // append the new background block, fill empty fields, upgrade
      // record_type to lead. Existing values are never overwritten.
      const mergeResults = await Promise.allSettled(
        toMerge.map(async (row) => {
          const existing = existingContacts.get(row.email);
          if (!existing) throw new Error(`${row.email}: contact disappeared`);

          const newTagIds = parseTags(row.tags)
            .map((name) => tags.get(name))
            .filter((tag): tag is Tag => !!tag)
            .map((tag) => tag.id);
          const mergedTags = [
            ...new Set([...(existing.tags ?? []), ...newTagIds]),
          ];
          const company = row.company?.trim()
            ? companies.get(row.company.trim())
            : undefined;
          const background = [existing.background, row.background]
            .filter(Boolean)
            .join("\n\n");

          return dataProvider.update("contacts", {
            id: existing.id,
            data: {
              tags: mergedTags,
              background,
              last_seen: today,
              title: existing.title || row.title || existing.title,
              phone: existing.phone || row.phone || existing.phone,
              company_id: existing.company_id ?? company?.id,
              record_type:
                row.record_type === "lead" &&
                (existing as { record_type?: string }).record_type === "network"
                  ? "lead"
                  : (existing as { record_type?: string }).record_type,
            },
            previousData: existing,
          });
        }),
      );
      mergeResults.forEach((result, index) => {
        if (result.status === "rejected") {
          const email = toMerge[index]?.email ?? "Unknown contact";
          const reason =
            result.reason instanceof Error
              ? result.reason.message
              : String(result.reason);
          errors.push(`${email} (merge): ${reason}`);
        }
      });
      const mergedCount = mergeResults.filter(
        (result) => result.status === "fulfilled",
      ).length;
      if (mergedCount > 0) {
        errors.push(
          `${mergedCount} contact(s) already existed and were merged (tags + notes added).`,
        );
      }

      // Optional Loops push: create/refresh each successfully imported
      // contact in Loops with full show-lead properties. The edge function
      // never overwrites existing Loops subscription state and stamps
      // loops_contact_id back onto the CRM rows.
      if (showLead && pushToLoops) {
        const pushedEmails = [
          ...toCreate.filter((_, i) => results[i]?.status === "fulfilled"),
          ...toMerge.filter((_, i) => mergeResults[i]?.status === "fulfilled"),
        ]
          .map((row) => loopsByEmail.get(row.email))
          .filter((c): c is LoopsImportContact => !!c);

        if (pushedEmails.length > 0) {
          try {
            const { results: loopsResults } = await (
              dataProvider as CrmDataProvider
            ).importContactsToLoops(pushedEmails);
            for (const r of loopsResults) {
              if (!r.success) {
                errors.push(`${r.email} (Loops): ${r.error ?? "failed"}`);
              }
            }
          } catch (e) {
            errors.push(
              `Loops push failed for this batch: ${
                e instanceof Error ? e.message : String(e)
              }. CRM import unaffected; re-run later with "Link to Loops".`,
            );
          }
        }
      }

      results.forEach((result, index) => {
        if (result.status === "rejected") {
          const email = toCreate[index]?.email ?? "Unknown contact";
          const reason =
            result.reason instanceof Error
              ? result.reason.message
              : String(result.reason);
          errors.push(`${email}: ${reason}`);
        }
      });

      return {
        imported:
          results.filter((result) => result.status === "fulfilled").length +
          mergedCount,
        errors,
      };
    },
    [
      dataProvider,
      getCompanies,
      getTags,
      getExistingContacts,
      user?.identity?.id,
      today,
      pushToLoops,
    ],
  );

  return processBatch;
}

const parseTags = (tags: string) =>
  tags
    ?.split(",")
    ?.map((tag: string) => tag.trim())
    ?.filter((tag: string) => tag) ?? [];

// Reads the bookmarks cell the contact export writes. Keeps each entry that is a
// {label, url} with both non-blank, the same rule the database enforces, and drops the
// rest; a cell that is not a JSON array gives none. Never fails the row: a mangled cell
// should not cost the contact.
// LCATOMICZO-83.
export const parseBookmarks = (cell: string | undefined): Bookmark[] => {
  if (!cell?.trim()) return [];
  let value: unknown;
  try {
    value = JSON.parse(cell);
  } catch {
    return [];
  }
  if (!Array.isArray(value)) return [];
  const isBookmark = (b: unknown): b is Bookmark =>
    typeof b === "object" &&
    b !== null &&
    typeof (b as Bookmark).label === "string" &&
    typeof (b as Bookmark).url === "string" &&
    /\S/.test((b as Bookmark).label) &&
    /\S/.test((b as Bookmark).url);
  return value.filter(isBookmark).map(({ label, url }) => ({ label, url }));
};
